// The customer portal's Training tab (Session AFL), on the site detail page. One card per
// COMPLETED training session ELE delivered at this site, each with its certificate download.
//
// The server sends a strict allow-list DTO, so this component could not render an internal field
// even if it tried: there is no deliveredBy, no ELE-internal notes, no attendee name, role, email
// or signature in the payload. Never add one.
//
// The data loads LAZILY, the first time the tab is opened. The sibling history tabs share one
// siteHistory call made on mount, but training is a separate endpoint most visits never need, and
// loading it on demand keeps the site page's first paint exactly as it is today.
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import {
  trainingTypeLabel,
  trainingRenewalState,
  type PortalTrainingSessionDto,
  type PortalTrainingDocGroup,
} from "../../lib/trainingTypes";
import { ContactValue } from "../../components/ContactValue";

// UK long form, e.g. "6 July 2026". Read in UTC deliberately: a training date is a calendar fact
// stored at UTC midnight, so a device in another time zone must not shift it to the day before
// (the server's own ukDate uses the UTC getters for exactly this reason).
export function trainingDateDisplay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" });
}

/** "1 staff member trained" / "4 staff trained". Zero is not shown at all (see the card). */
export function attendeeCountLine(count: number): string {
  return count === 1 ? "1 staff member trained" : `${count} staff trained`;
}

// A session date as a sortable number. An unreadable date sorts oldest, so it can never win the
// "newest" comparison below and silently take the renewal line off a real session.
function sessionTime(session: PortalTrainingSessionDto): number {
  const t = new Date(session.sessionDate).getTime();
  return Number.isNaN(t) ? -Infinity : t;
}

/**
 * The ids of the sessions whose renewal line still means something: the NEWEST session of each
 * kind of training, PER SITE.
 *
 * Training gets redelivered. Once it has been, the older session's two-year renewal date is
 * superseded, and the portal must stop counting down against it: telling a customer their
 * hand-winding training lapsed in March when ELE redelivered it in April is a compliance failure
 * that never happened, on the one screen they would act on. The older sessions stay visible as
 * history, they just carry no renewal prompt.
 *
 * Keyed on the training type, since that is what a refresher replaces. OTHER carries no fixed
 * meaning, so it is keyed on its customer-facing label too: a "Machine room access briefing" does
 * not supersede an unrelated bespoke session that also happens to be typed OTHER.
 *
 * AND KEYED ON THE SITE, whenever the caller knows it (Session AGJ). This was safe while the only
 * caller was the per-site tab, where every session shares one site. On the cross-site feed it
 * inverts the whole point of the function: hand-winding redelivered at Site A would take the
 * renewal prompt off Site B's hand-winding session, which is the same "compliance failure that
 * never happened" in the other direction, and worse, because it HIDES a real lapse instead of
 * inventing one. Training is delivered to the staff at a building, so it supersedes only within
 * that building.
 *
 * siteId is optional so the per-site tab is unaffected: its DTO carries none, every session shares
 * a site, and the keying collapses to exactly what it was before.
 */
export function currentRenewalSessionIds(sessions: (PortalTrainingSessionDto & { siteId?: string })[]): Set<string> {
  const newest = new Map<string, PortalTrainingSessionDto & { siteId?: string }>();
  for (const s of sessions) {
    const typeKey = s.trainingType === "OTHER" ? `OTHER:${(s.customLabel ?? "").trim().toLowerCase()}` : s.trainingType;
    const key = s.siteId ? `${s.siteId}|${typeKey}` : typeKey;
    const held = newest.get(key);
    // Compared explicitly rather than trusting the order the server sent them in.
    if (!held || sessionTime(s) > sessionTime(held)) newest.set(key, s);
  }
  return new Set([...newest.values()].map((s) => s.id));
}

export function PortalTrainingPanel({ siteId }: { siteId: string }) {
  const [sessions, setSessions] = useState<PortalTrainingSessionDto[] | null>(null);
  const [failed, setFailed] = useState(false);
  const renewalIds = useMemo(() => currentRenewalSessionIds(sessions ?? []), [sessions]);

  useEffect(() => {
    let live = true;
    setSessions(null);
    setFailed(false);
    api.portal
      .siteTraining(siteId)
      .then((res) => {
        if (!live) return;
        setSessions(res.sessions);
      })
      .catch(() => {
        if (!live) return;
        setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [siteId]);

  // The completed-sessions area, rendered BELOW the reference documents in every state. A failed
  // session load never hides the reference documents (they load independently), and vice versa.
  let sessionsContent;
  if (failed) {
    sessionsContent = (
      <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">
        This section could not be loaded. Please refresh to try again.
      </p>
    );
  } else if (!sessions) {
    sessionsContent = <p className="text-sm text-ele-body">Loading...</p>;
  } else if (sessions.length === 0) {
    // Not an error: a site with no training records is the normal starting state, so the panel says
    // so plainly and offers the one next step.
    //
    // DELIBERATE DEVIATION from the session prompt's verbatim copy, for state-neutral honesty: this
    // panel shows COMPLETED sessions only, so a customer whose training is already in the diary saw
    // "No training records yet" and was told to ring and arrange what ELE had arranged with them.
    // The wording now describes the tab rather than asserting nothing is booked. The Director may
    // revert it.
    sessionsContent = (
      <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">
        <p className="text-ele-white">Training records appear here once a session has been completed.</p>
        <p className="mt-1">
          To arrange training for your team, contact us on{" "}
          <ContactValue value="0203 576 4760" kind="phone" className="text-ele-green hover:underline" />.
        </p>
      </div>
    );
  } else {
    sessionsContent = (
      <div className="space-y-3">
        {sessions.map((s) => (
          <TrainingCard key={s.id} session={s} showRenewal={renewalIds.has(s.id)} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ReferenceDocsSection />
      {sessionsContent}
    </div>
  );
}

// The tenant's CURRENT, CUSTOMER-FACING training reference documents, grouped by type. Tenant-global
// (not per-site): a hand-winding procedure is generic to the equipment, so it is shown wherever a
// customer looks, even with no session on record. Silent when there are none: no empty section.
export function ReferenceDocsSection() {
  const [groups, setGroups] = useState<PortalTrainingDocGroup[] | null>(null);

  useEffect(() => {
    let live = true;
    // Promise.resolve().then(...) so a partial api mock or any hiccup can never break the panel.
    Promise.resolve()
      .then(() => api.portal.trainingDocuments())
      .then((r) => {
        if (live) setGroups(r.groups);
      })
      .catch(() => {
        if (live) setGroups([]);
      });
    return () => {
      live = false;
    };
  }, []);

  if (!groups || groups.length === 0) return null;

  return (
    <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4">
      <h3 className="font-heading text-lg text-ele-white">Reference documents</h3>
      <p className="mt-1 text-sm text-ele-body">On-site procedures for your team to follow. Download and keep them to hand.</p>
      <div className="mt-3 space-y-3">
        {groups.map((g) => (
          <div key={g.trainingType}>
            <p className="font-menu text-xs uppercase tracking-wide text-ele-body">{g.label}</p>
            <ul className="mt-1 space-y-1">
              {g.documents.map((d) => (
                <li key={d.id}>
                  <a
                    href={api.portal.trainingDocumentHref(d.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ele-green hover:underline"
                  >
                    {d.title}
                    {d.version ? ` (${d.version})` : ""}
                  </a>
                  <span className="ml-2 text-xs text-ele-body">{d.kind}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// Exported (Session AGJ) so the top-level Training page renders the SAME card rather than a second
// copy that would drift on the renewal wording, the certificate link or the no-alarm colour rule.
// `siteName` is passed only by the cross-site feed, where a card has to say which building it was at.
export function TrainingCard({
  session,
  showRenewal,
  siteName,
}: {
  session: PortalTrainingSessionDto;
  showRenewal: boolean;
  siteName?: string;
}) {
  const completed = trainingDateDisplay(session.sessionDate);
  // Only the newest session of this kind of training carries a live renewal date; an older one
  // has been superseded by the refresher, so it shows no renewal line at all.
  const renewal = showRenewal ? trainingRenewalState(session.renewalDueDate) : null;
  const renewalDate = trainingDateDisplay(session.renewalDueDate);

  return (
    <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4">
      <h3 className="font-heading text-lg text-ele-white">{trainingTypeLabel(session.trainingType, session.customLabel)}</h3>

      {/* Only on the cross-site feed: on a site's own tab every card is the same site, so naming it
          on each card would be noise. */}
      {siteName && <p className="mt-1 text-sm text-ele-body">{siteName}</p>}

      {completed && <p className="mt-1 text-sm text-ele-body">Completed {completed}</p>}

      {/* Only shown when the session was recorded against specific lifts; a general procedure
          session carries none and the line is omitted rather than printed empty. */}
      {session.liftEleNumbers.length > 0 && (
        <p className="mt-1 text-sm text-ele-body">Covering: {session.liftEleNumbers.join(", ")}</p>
      )}

      {session.attendeeCount > 0 && <p className="mt-1 text-sm text-ele-body">{attendeeCountLine(session.attendeeCount)}</p>}

      {session.certificateRef && (
        <p className="mt-1 text-sm text-ele-body">
          Certificate <span className="font-heading tracking-wide text-ele-loler">{session.certificateRef}</span>
        </p>
      )}

      {/* Renewal is guidance, never an alarm: a due-soon or overdue refresher is amber with a way
          to act, and a session with no renewal date shows no line at all. */}
      {renewal === "ok" && renewalDate && <p className="mt-2 text-sm text-ele-body">Renewal recommended by {renewalDate}</p>}
      {renewal === "due_soon" && (
        <p className="mt-2 text-sm text-amber-400">Renewal due soon - contact us to arrange</p>
      )}
      {renewal === "overdue" && (
        <p className="mt-2 text-sm text-amber-400">
          Renewal overdue - contact us on{" "}
          <ContactValue value="0203 576 4760" kind="phone" className="underline" />
        </p>
      )}

      {session.customerNotes && <p className="mt-2 whitespace-pre-wrap text-sm text-ele-body">{session.customerNotes}</p>}

      {/* The certificate is generated on demand by the ownership-scoped, audited proxy; a session
          with no issued reference has nothing to download, so no button is offered. */}
      {session.certificateRef && (
        <a
          href={api.portal.trainingCertificateHref(session.id)}
          className="mt-3 inline-flex w-full items-center justify-center rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg transition hover:brightness-110 sm:w-auto"
        >
          Download certificate
        </a>
      )}
    </div>
  );
}
